import {
  MS_PER_DAY,
  USER_SHEET_DEFAULT_COMMS_WIDTH_PERCENT,
} from "@/lib/constants";
import {
  DashboardHideableColumn,
  GoalPeriod,
  JobVisit,
  PostSignInResponse,
  UserSheetFocusMode,
} from "@/types";

const KEY_GOALS_PERIOD = "dashboardGoalsPeriod";
const KEY_JOB_VISITS = "jobVisits";
const KEY_INPUT_DRAFT = "inputDraft";
const KEY_HIDDEN_DASHBOARD_COLUMNS = "hiddenDashboardColumns";
const KEY_HIDDEN_DASHBOARD_ALL_COLUMNS = "hiddenDashboardAllColumns";
const KEY_SEARCH_REVIEW_VIEWED_JOB_IDS = "searchReviewViewedJobIds";
const KEY_SEARCH_SHEET_COMMS_OPEN = "searchSheetCommsOpen";
const KEY_USER_SHEET_FOCUS_MODE = "userSheetFocusMode";
const KEY_USER_SHEET_FOCUS_MODE_ACTIVE = "userSheetFocusModeActive";
const SEARCH_REVIEW_VIEWED_TTL_MS = 30 * MS_PER_DAY;
// One entry per (user, job) still in the presence window, so this only has to
// hold a busy team's recent hops. Oldest entries fall off first.
const MAX_JOB_VISITS = 200;

// Parses a mirrored hidden-column set, dropping anything that isn't a known
// enum value.
const readHiddenColumns = (key: string): DashboardHideableColumn[] => {
  const stored = localStorage.getItem(key);
  if (!stored) return [];
  const valid = new Set<string>(Object.values(DashboardHideableColumn));
  try {
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (column): column is DashboardHideableColumn =>
        typeof column === "string" && valid.has(column),
    );
  } catch {
    return [];
  }
};

// jobId → expiresAt, with expired entries pruned on read.
const readSearchReviewViewedEntries = (): Record<string, number> => {
  const stored = localStorage.getItem(KEY_SEARCH_REVIEW_VIEWED_JOB_IDS);
  if (!stored) return {};
  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== "object" || parsed === null) return {};
    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === "number" && entry[1] > now,
      ),
    );
  } catch {
    return {};
  }
};
const KEY_TURBO_MODE = "turbo-mode";
const KEY_USER = "user";

export const storage = {
  clear: () => {
    localStorage.clear();
  },

  // In-progress drafts for long-form inputs (notes, evaluations) — cleared on
  // save or when the text is emptied //
  getInputDraft: (key: string): string =>
    localStorage.getItem(`${KEY_INPUT_DRAFT}:${key}`) ?? "",
  setInputDraft: (key: string, draft: string) => {
    const storageKey = `${KEY_INPUT_DRAFT}:${key}`;
    if (!draft) {
      localStorage.removeItem(storageKey);
      return;
    }
    localStorage.setItem(storageKey, draft);
  },

  // Search sheet comms rail — reopens the way it was last used this session //
  getSearchSheetCommsOpen: (): boolean =>
    sessionStorage.getItem(KEY_SEARCH_SHEET_COMMS_OPEN) === "true",
  setSearchSheetCommsOpen: (isOpen: boolean) => {
    sessionStorage.setItem(KEY_SEARCH_SHEET_COMMS_OPEN, String(isOpen));
  },

  // Dashboard Goals Period — remembers Month vs Week view across visits //
  getGoalsPeriod: (): GoalPeriod => {
    const stored = localStorage.getItem(KEY_GOALS_PERIOD);
    return stored === GoalPeriod.WEEK ? GoalPeriod.WEEK : GoalPeriod.MONTH;
  },
  setGoalsPeriod: (period: GoalPeriod) => {
    localStorage.setItem(KEY_GOALS_PERIOD, period);
  },

  // Dashboard hidden columns — mirrored from user_settings so the table skeleton
  // renders the right column shell before the settings request resolves //
  getHiddenDashboardColumns: (): DashboardHideableColumn[] =>
    readHiddenColumns(KEY_HIDDEN_DASHBOARD_COLUMNS),
  setHiddenDashboardColumns: (columns: DashboardHideableColumn[]) => {
    localStorage.setItem(KEY_HIDDEN_DASHBOARD_COLUMNS, JSON.stringify(columns));
  },
  getHiddenDashboardAllColumns: (): DashboardHideableColumn[] =>
    readHiddenColumns(KEY_HIDDEN_DASHBOARD_ALL_COLUMNS),
  setHiddenDashboardAllColumns: (columns: DashboardHideableColumn[]) => {
    localStorage.setItem(
      KEY_HIDDEN_DASHBOARD_ALL_COLUMNS,
      JSON.stringify(columns),
    );
  },

  // Search review viewed state — a job is "viewed" once its search review was
  // opened, from the dashboard or the search page. Entries expire after 30
  // days so stale views resurface as unviewed //
  getSearchReviewViewedJobIds: (): string[] =>
    Object.keys(readSearchReviewViewedEntries()),
  setSearchReviewViewed: (jobId: string) => {
    const entries = readSearchReviewViewedEntries();
    entries[jobId] = Date.now() + SEARCH_REVIEW_VIEWED_TTL_MS;
    localStorage.setItem(
      KEY_SEARCH_REVIEW_VIEWED_JOB_IDS,
      JSON.stringify(entries),
    );
  },

  // Job presence history //
  // Oldest-first, so trimming to the cap is a plain FIFO drop from the front.
  getJobVisits: (): JobVisit[] => {
    const stored = localStorage.getItem(KEY_JOB_VISITS);
    if (!stored) return [];
    try {
      const parsed: unknown = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(
          (visit): visit is JobVisit =>
            typeof visit === "object" &&
            visit !== null &&
            typeof (visit as JobVisit).userId === "number" &&
            typeof (visit as JobVisit).jobId === "string" &&
            typeof (visit as JobVisit).lastSeenAtMs === "number",
        )
        // firstSeenAtMs arrived later — entries saved before it start their
        // clock at the last sighting rather than being thrown away.
        .map((visit) => ({
          ...visit,
          firstSeenAtMs:
            typeof visit.firstSeenAtMs === "number"
              ? visit.firstSeenAtMs
              : visit.lastSeenAtMs,
        }));
    } catch {
      return [];
    }
  },
  setJobVisits: (visits: JobVisit[]) => {
    const capped = visits.slice(-MAX_JOB_VISITS);
    localStorage.setItem(KEY_JOB_VISITS, JSON.stringify(capped));
  },

  // Turbo Mode //
  getTurboMode: (): boolean => {
    return localStorage.getItem(KEY_TURBO_MODE) === "true";
  },
  setTurboMode: (enabled: boolean) => {
    localStorage.setItem(KEY_TURBO_MODE, String(enabled));
  },

  // User Sheet Focus Mode — one preferred sheet layout per signed-in staffer //
  getUserSheetFocusMode: (userId: number): UserSheetFocusMode | undefined => {
    const stored = localStorage.getItem(
      `${KEY_USER_SHEET_FOCUS_MODE}:${userId.toString()}`,
    );
    if (!stored) return undefined;

    try {
      const parsed = JSON.parse(stored) as Partial<UserSheetFocusMode>;
      if (
        typeof parsed.activeTab !== "string" ||
        typeof parsed.isCommunicationsOpen !== "boolean" ||
        !Array.isArray(parsed.splitTabValues) ||
        !parsed.splitTabValues.every((value) => typeof value === "string")
      ) {
        return undefined;
      }
      const splitTabValues = parsed.splitTabValues;
      // Collapse/zoom fields arrived later — older saves fall back to the
      // pre-focus-mode defaults rather than invalidating the whole entry.
      return {
        activeTab: parsed.activeTab,
        communicationsScope:
          typeof parsed.communicationsScope === "string"
            ? parsed.communicationsScope
            : null,
        communicationsWidthPercent:
          typeof parsed.communicationsWidthPercent === "number"
            ? parsed.communicationsWidthPercent
            : USER_SHEET_DEFAULT_COMMS_WIDTH_PERCENT,
        isActivityRailCollapsed: parsed.isActivityRailCollapsed === true,
        isCommunicationsOpen: parsed.isCommunicationsOpen,
        isResumesCollapsed: parsed.isResumesCollapsed === true,
        paneWidthsPercent:
          Array.isArray(parsed.paneWidthsPercent) &&
          parsed.paneWidthsPercent.length === splitTabValues.length + 1 &&
          parsed.paneWidthsPercent.every((value) => typeof value === "number")
            ? parsed.paneWidthsPercent
            : Array.from(
                { length: splitTabValues.length + 1 },
                () => 100 / (splitTabValues.length + 1),
              ),
        resumeZoom:
          typeof parsed.resumeZoom === "number" ? parsed.resumeZoom : null,
        splitTabValues,
      };
    } catch {
      return undefined;
    }
  },
  setUserSheetFocusMode: (userId: number, focusMode: UserSheetFocusMode) => {
    localStorage.setItem(
      `${KEY_USER_SHEET_FOCUS_MODE}:${userId.toString()}`,
      JSON.stringify(focusMode),
    );
  },
  // Whether the staffer left focus mode on — the next sheet reopens with it //
  getUserSheetFocusModeActive: (userId: number): boolean =>
    localStorage.getItem(
      `${KEY_USER_SHEET_FOCUS_MODE_ACTIVE}:${userId.toString()}`,
    ) === "true",
  setUserSheetFocusModeActive: (userId: number, isActive: boolean) => {
    localStorage.setItem(
      `${KEY_USER_SHEET_FOCUS_MODE_ACTIVE}:${userId.toString()}`,
      String(isActive),
    );
  },

  // User //
  getUser: (): PostSignInResponse | null => {
    const user = localStorage.getItem(KEY_USER);
    if (!user) return null;

    // TODO: Better way to verify type.
    return JSON.parse(user) as PostSignInResponse;
  },
  setUser: (user: PostSignInResponse) => {
    localStorage.setItem(KEY_USER, JSON.stringify(user));
  },
};
